import { ApiError } from "@/lib/api-client";

// Maps a thrown get_resource_download_url failure to UI copy + a refresh hint.
// Mirrors routes/clients/client-error.ts: branch ONLY on the structured
// `error.code` — never on the server's `message` text. Messages are our own,
// PHI-free copy (a document id is not patient data, but we still never echo
// server strings).
//
// [Source: src/application/reference_data/get_resource_download_url/exceptions.py —
//  RES-404 (ResourceNotFoundException, 404), RES-503
//  (ResourceDownloadConfigUnavailableException, 503); endpoint.py also returns a
//  400 (InvalidUUIDException) for a malformed id, which falls through to generic.]

export interface ResourceDownloadError {
  /** Inline, PHI-free message rendered under the row's Download button (role="alert"). */
  message: string;
  /** True when the row no longer exists server-side: the list is stale and must refetch. */
  shouldInvalidate: boolean;
}

/** Map a failed download-url mint to inline copy + whether to refetch the list. */
export function toResourceDownloadError(error: unknown): ResourceDownloadError {
  if (error instanceof ApiError) {
    if (error.code === "RES-404") {
      // The document vanished server-side (superseded/removed). Tell the user and
      // refetch so the stale row drops out of the table.
      return {
        message: "This resource is no longer available.",
        shouldInvalidate: true,
      };
    }
    if (error.code === "RES-503") {
      // Download infra not wired yet (expected pre-deploy) — transient, retryable.
      return {
        message: "Downloads are temporarily unavailable — please try again shortly.",
        shouldInvalidate: false,
      };
    }
  }
  // 400 (malformed id), NETWORK_ERROR, unknown → generic. 401 never reaches here:
  // apiFetch handles it via silent refresh (or surfaces AuthExpiredError → sign-in).
  return {
    message: "Couldn't start the download — please try again.",
    shouldInvalidate: false,
  };
}
